import React, { useState, useEffect, useRef } from 'react';
import { Button, Modal, Stack, Form, Image } from 'react-bootstrap';
import Calendar from 'react-calendar';
import Slider from 'react-slick';
import useCommonStore, { setIsLoading } from 'store/common';
import { formatDateForMeeting, S3_URL } from 'constants/index';
import {
	useGetCompanySeekerAvailabilityQuery,
	useGetGoogleAuthUrlMutation,
	useGetTimeSlotsQuery,
	useRefreshTokenUpdateMutation,
	useScheduleInterviewMutation,
} from 'services';
import moment from 'moment';
import toast from 'react-hot-toast';
import { formatAvailableDates, isTimeGone } from 'utils';
import useTranslate from 'hooks/useTranslate';
import { IMAGE_PATHS } from 'constants/imagePaths';
import { secureStorage } from 'utils/secureStorage';

const ScheduleMeetingCompanyModal: React.FC<any> = ({ showModal }) => {
	const { translate } = useTranslate();
	const { mutateAsync: getGoogleAuthUrl } = useGetGoogleAuthUrlMutation();
	const { data: { mettingTimeSloat: mettingTimeSlot = [] } = {} } = useGetTimeSlotsQuery();
	const { modalConfig = {}, hideCommonModal }: any = useCommonStore((state) => state);
	const currentDate = new Date();
	const [payload, setPayload] = useState({
		date: formatDateForMeeting(currentDate),
		month: currentDate.toLocaleString('default', { month: 'long' }),
		year: currentDate.getFullYear().toString(),
		userId: modalConfig?.id,
	});

	const { data, refetch } = useGetCompanySeekerAvailabilityQuery(payload);
	const [selectedSlots, setSelectedSlots] = useState<string[]>([]);
	const [value, setValue] = useState<any>(formatDateForMeeting(new Date())); // State for the selected date
	const [highlightDates, setHighlightDates] = useState<string[]>([]);
	const [selectedSlot, setSelectedSlot] = useState<string>('');
	const previousMonthRef = useRef<string>(payload.month); // Ref to store previous month
	const { mutateAsync: scheduleInterviewMutation } = useScheduleInterviewMutation();
	const { mutateAsync: refreshTokenUpdateMutation } = useRefreshTokenUpdateMutation();
	const [agenda, setAgenda] = useState(modalConfig?.data?.companyDesc);
	const [agendaDisabled, setAgendaDisabled] = useState(true);

	// Settings for the slider
	const settings = {
		dots: false,
		infinite: false,
		speed: 500,
		slidesToShow: 9,
		slidesToScroll: 3,
		vertical: true,
		verticalSwiping: true,
		responsive: [
			{
				breakpoint: 767,
				settings: {
					slidesToShow: 6,
				},
			},
			{
				breakpoint: 575,
				settings: {
					slidesToShow: 4,
				},
			},
		],
	};

	// Update the payload and selected slots when the modal opens
	useEffect(() => {
		if (showModal) {
			setPayload((prevPayload) => ({
				...prevPayload,
				date: formatDateForMeeting(currentDate),
				month: currentDate.toLocaleString('default', { month: 'long' }),
				year: currentDate.getFullYear().toString(),
			}));
			setValue(currentDate);
		}
	}, [showModal, currentDate]);

	// Update selected slots based on fetched data when date or data changes
	useEffect(() => {
		if (data && data.length > 0) {
			const availability: any = data.find(
				(item: any) => moment(item.date).format('MM/DD/YYYY') === value,
			);
			setSelectedSlots(availability ? availability?.timeSlots : []);
			setHighlightDates(data.map((item: any) => moment(item.date).format('MM/DD/YYYY')));
		} else {
			setHighlightDates([]);
		}
	}, [data, value]);

	// Refetch data only when the month changes
	useEffect(() => {
		if (previousMonthRef.current !== payload.month) {
			refetch();
			previousMonthRef.current = payload.month; // Update the ref to the new month
		}
	}, [payload, refetch]);

	// Handle time slot click
	const handleTimeSlotClick = (slot: string) => {
		const today = moment().format('MM/DD/YYYY');
		if (selectedSlots?.includes(slot)) {
			// If slot is already selected, remove it
			const selected = selectedSlots.filter((selectedSlot) => selectedSlot === slot);
			if (today === payload?.date) {
				if (!isTimeGone(slot)) {
					setSelectedSlot(selected[0]);
				}
			} else {
				setSelectedSlot(selected[0]);
			}
		}
	};

	// Handle date change
	const handleDateChange = (newValue: any) => {
		setSelectedSlot('');
		const newDate = moment(newValue).toISOString();
		setValue(moment(newDate).format('MM/DD/YYYY'));
		const formattedDate = formatDateForMeeting(newValue);
		setPayload((prevPayload) => ({
			...prevPayload,
			date: formattedDate,
			month: newValue.toLocaleString('default', { month: 'long' }),
			year: newValue.getFullYear().toString(),
		}));
		const availability = data?.filter(
			(res: any) => moment(res.date).format('MM/DD/YYYY') === moment(newDate).format('MM/DD/YYYY'),
		);
		setSelectedSlots(availability ? availability[0]?.timeSlots : []);
	};

	const handleMonthChange = (newValue: any) => {
		setSelectedSlot(''); // Clear the selected slot

		// Get today's date
		const today = new Date();
		today.setHours(0, 0, 0, 0); // Reset time to midnight

		// Get the active start date of the new month (first day of the month)
		const activeStartDate = newValue?.activeStartDate;

		// Check if today's date is within the current month
		const isTodayInCurrentMonth =
			today.getFullYear() === activeStartDate.getFullYear() &&
			today.getMonth() === activeStartDate.getMonth();

		// If today is in the selected month, set value to today; otherwise, set to the active start date of the month
		const newDate = isTodayInCurrentMonth ? today : activeStartDate;
		setValue(newDate); // Set calendar value

		const formattedDate = formatDateForMeeting(newDate);
		setPayload((prevPayload) => ({
			...prevPayload,
			date: formattedDate,
			month: newDate.toLocaleString('default', { month: 'long' }),
			year: newDate.getFullYear().toString(),
		}));
	};

	// Highlight only future dates including today
	const highlightTileClassName = ({ date, view }: any) => {
		const today = new Date();
		today.setHours(0, 0, 0, 0); // Reset time to midnight
		if (view === 'month') {
			const formattedDate = formatDateForMeeting(date);
			const isFutureOrToday = date >= today; // Include today and future dates
			if (highlightDates.includes(formattedDate) && isFutureOrToday) {
				return 'highlight-date';
			}
		}
		return date < today ? 'disabled-date' : null; // Only disable dates before today
	};

	// Disable past dates only
	const disablePastDates = ({ date }: any) => {
		const today = new Date();
		today.setHours(0, 0, 0, 0); // Reset time to midnight
		return date < today; // Disable only dates before today
	};

	const sendMeetingLink = async () => {
		if (agenda?.length > 0) {
			setIsLoading(true);
			const payloadForInterview: any = {
				jobId: Number(modalConfig?.data?.jobId),
				seekerId: modalConfig?.id,
				// date: moment(payload?.date).toISOString(),
				date: payload?.date,
				time: selectedSlot,
				title: agenda,
			};

			const refreshResponse = await refreshTokenUpdateMutation();
			if (refreshResponse?.success) {
				const scheduleResponse = await scheduleInterviewMutation(payloadForInterview);
				if (scheduleResponse?.success) {
					toast.success(translate('ToastMessage.interViewLinkSent'));
					setIsLoading(false);
					hideCommonModal();
				} else {
					setIsLoading(false);
				}
			} else {
				setIsLoading(false);
				secureStorage.setItem('payloadForInterview', payloadForInterview);
				getGoogleAuthUrl()
					.then((res: any) => {
						window.location.href = res?.data?.authUrl;
					})
					.catch((_err: any) => {});
			}
		} else {
			toast.error('Please add Meeting Agenda First!');
		}
	};

	const handleKeyDown = (e: any) => {
		if (e.keyCode === 13) {
			setAgendaDisabled(true);
		}
	};

	return (
		<Modal.Body
			onClick={() => {
				setAgendaDisabled(true);
			}}
		>
			<Button
				className="close-btn rounded-circle border-0 fw-bold position-absolute"
				onClick={() => hideCommonModal()}
			>
				<i className="ri-close-line"></i>
			</Button>
			<Form className="edit-form">
				<Stack className="gap-4">
					<div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-2">
						<h4 className="fw-bold mb-0">{translate('Interview.scheduleInterview')}</h4>
						<div className="profile-content d-flex align-items-center gap-3">
							<div className="flex-1 text-lg-end">
								<h5 className="fw-bold mb-1">{modalConfig?.data?.userName}</h5>
								<p className="mb-0">{formatAvailableDates(highlightDates, translate)}</p>
							</div>
							<div className="profile-content-img">
								<Image
									src={
										modalConfig?.data?.profilePicture
											? `${S3_URL}${modalConfig.data.profilePicture}`
											: IMAGE_PATHS.UserDefault
									}
									onError={({ currentTarget }) => {
										currentTarget.onerror = null;
										currentTarget.src = IMAGE_PATHS.UserDefault;
									}}
									alt="profile"
									className="rounded-circle w-100 h-100 object-fit-cover"
									loading="lazy"
								/>
							</div>
						</div>
					</div>

					<div className="agenda-box text-center">
						<h5 className="fw-bold">{translate('Common.meetingAgenda')}</h5>
						{!agendaDisabled ? (
							<Form.Control
								type="text"
								placeholder={translate('Common.meetingAgenda')}
								className="rounded-pill"
								onClick={(e: any) => e.stopPropagation()}
								name="title"
								autoComplete="off"
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
									let inputValue = e.target.value;

									// Remove leading spaces if present
									inputValue = inputValue.replace(/^\s+/, '');

									// Allow only one space between words
									inputValue = inputValue.replace(/\s{2,}/g, ' ');
									setAgenda(inputValue);
								}}
								value={agenda}
								onKeyDown={handleKeyDown}
								onBlur={() => {
									setAgendaDisabled(true);
								}}
							/>
						) : (
							<p
								onClick={(e: any) => {
									e.stopPropagation();
								}}
								className="mb-0"
							>
								{agenda.length > 0 ? agenda : translate('Common.meetingAgenda')}
								<i
									style={{ cursor: 'pointer', padding: '0 10px' }}
									onClick={(e: any) => {
										e.stopPropagation();
										setAgendaDisabled(false);
									}}
									className="ri-pencil-line"
								></i>
							</p>
						)}
					</div>

					<div className="select-date-time">
						<div className="d-flex align-items-center justify-content-between gap-2 mb-3">
							<h3 className="mb-0">{translate('Common.selectDateAndTime')}</h3>
							<Button
								onClick={sendMeetingLink}
								disabled={selectedSlot?.length === 0}
								className="filled-btn rounded-pill lh-1"
							>
								{translate('Common.confirm')}
							</Button>
						</div>
						<div className="d-flex flex-column flex-sm-row justify-content-between gap-4">
							<div className="select-time-box">
								<Slider {...settings} className="w-100 overflow-hidden">
									{mettingTimeSlot.length > 0 ? (
										mettingTimeSlot?.map((res: any, index: any) => (
											<div
												key={`${res}-${index}`}
												onClick={() => handleTimeSlotClick(res)}
												className={`${[selectedSlot]?.includes(res) && 'selected-slot'} time-box rounded-pill bg-white text-center  ${selectedSlots?.includes(res) ? 'selected hovered' : ''}`}
											>
												{res}
											</div>
										))
									) : (
										<p>{translate('Common.noDataFound')}</p>
									)}
								</Slider>
							</div>
							<div className="date-box">
								<Calendar
									minDate={new Date()}
									onChange={handleDateChange}
									value={value}
									onActiveStartDateChange={handleMonthChange}
									tileClassName={highlightTileClassName}
									tileDisabled={disablePastDates} // Disable past dates
								/>
							</div>
						</div>
					</div>
				</Stack>
			</Form>
		</Modal.Body>
	);
};

export default ScheduleMeetingCompanyModal;
